import { useState } from 'react';
import { Check, ChevronsUpDown, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCompany } from '@/contexts/CompanyContext';
import { cn } from '@/lib/utils';

interface CompanySelectorProps {
  /** When true, renders a full-width sidebar-style button with name + code stacked */
  sidebarMode?: boolean;
  collapsed?: boolean;
}

export function CompanySelector({ sidebarMode = false, collapsed = false }: CompanySelectorProps) {
  const { currentCompany, availableCompanies, switchCompany, isLoading } = useCompany();
  const [open, setOpen] = useState(false);

  const handleSelect = (companyId: string) => {
    if (companyId === currentCompany?.id) {
      setOpen(false);
      return;
    }
    try {
      switchCompany(companyId);
      setOpen(false);
    } catch (error) {
      console.error('Failed to switch company:', error);
    }
  };

  /** e.g. "ACME • GBP" or just "ACME" */
  const companySubline = (code: string, instanceId?: string) =>
    instanceId ? `${code} • ${instanceId}` : code;

  /* ── Loading state ── */
  if (isLoading) {
    if (sidebarMode) {
      return (
        <div className="flex items-center gap-2 px-2 py-1.5 rounded-md">
          <Building2 className="h-4 w-4 text-sidebar-foreground/50 shrink-0" />
          <span className="text-sm text-sidebar-foreground/60">Loading…</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-to-r from-teal-500/15 to-teal-500/10 border-2 border-teal-500/30 shadow-sm">
        <Building2 className="h-3.5 w-3.5 text-teal-600" />
        <span className="font-bold text-teal-600 text-xs">Loading...</span>
      </div>
    );
  }

  if (!currentCompany || availableCompanies.length === 0) {
    return null;
  }

  /* ── Collapsed sidebar mode — icon + tooltip ── */
  if (collapsed && sidebarMode) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="w-full h-9 flex items-center justify-center hover:bg-sidebar-accent/50"
                disabled={isLoading}
              >
                <Building2 className="h-4 w-4 text-sidebar-foreground/60" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right">
            <p>{currentCompany?.name ?? 'Select company'}</p>
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="p-0 w-[280px]" side="right" align="end">
          <Command>
            <CommandInput placeholder="Search companies..." className="h-9" />
            <CommandList>
              <CommandEmpty>No company found.</CommandEmpty>
              <CommandGroup>
                {availableCompanies.map((company) => {
                  const isSelected = currentCompany?.id === company.id;
                  return (
                    <CommandItem
                      key={company.id}
                      value={`${company.name} ${company.code}`}
                      onSelect={() => handleSelect(company.id)}
                      className="cursor-pointer py-2.5 px-3 gap-2"
                    >
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0 text-teal-600',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className={cn('text-sm truncate leading-tight', isSelected ? 'font-semibold text-foreground' : 'font-medium text-foreground')}>
                          {company.name}
                        </span>
                        <span className="text-xs text-teal-600/80 leading-tight truncate">
                          {companySubline(company.code, company.instanceId)}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  /* ── Single company — no dropdown needed ── */
  if (availableCompanies.length === 1) {
    if (sidebarMode) {
      return (
        <div className="flex items-center gap-2 rounded-md border border-sidebar-border px-2 py-2 w-full">
          <Building2 className="h-4 w-4 text-sidebar-foreground/60 shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium text-sidebar-foreground truncate leading-tight">
              {currentCompany.name}
            </span>
            <span className="text-xs text-sidebar-foreground/50 leading-tight truncate">
              {companySubline(currentCompany.code, currentCompany.instanceId)}
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-to-r from-teal-500/15 to-teal-500/10 border-2 border-teal-500/30 shadow-sm">
        <Building2 className="h-3.5 w-3.5 text-teal-600" />
        <span className="font-bold text-teal-600 text-xs">{currentCompany.name}</span>
      </div>
    );
  }

  /* ── Multi-company dropdown ── */
  if (sidebarMode) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {/* Trigger — aligns with nav items: px-3 horizontal, full width */}
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            className="flex w-full items-center gap-2 rounded-md border border-sidebar-border px-2 py-2 text-left outline-none hover:bg-sidebar-accent/50 transition-colors duration-150 cursor-pointer"
          >
            {/* Small building icon — same size as nav icons */}
            <Building2 className="h-4 w-4 text-sidebar-foreground/60 shrink-0" />

            {/* Company name + subline */}
            <div className="flex flex-col items-start min-w-0 flex-1">
              <span className="text-sm font-medium text-sidebar-foreground truncate w-full leading-tight">
                {currentCompany.name}
              </span>
              <span className="text-xs text-sidebar-foreground/50 leading-tight truncate w-full">
                {companySubline(currentCompany.code, currentCompany.instanceId)}
              </span>
            </div>

            {/* Chevrons */}
            <ChevronsUpDown className="h-4 w-4 text-sidebar-foreground/40 shrink-0" />
          </button>
        </PopoverTrigger>

        {/* Dropdown — opens below, same width as the sidebar panel */}
        <PopoverContent
          className="p-0"
          align="start"
          side="bottom"
          sideOffset={4}
          style={{ width: 'var(--radix-popover-trigger-width)' }}
        >
          <Command>
            <CommandInput placeholder="Search companies..." className="h-9" />
            <CommandList>
              <CommandEmpty>No company found.</CommandEmpty>
              <CommandGroup>
                {availableCompanies.map((company) => {
                  const isSelected = currentCompany.id === company.id;
                  return (
                    <CommandItem
                      key={company.id}
                      value={`${company.name} ${company.code}`}
                      onSelect={() => handleSelect(company.id)}
                      className="cursor-pointer py-2.5 px-3 gap-2"
                    >
                      {/* Check mark — same width as icon space so names line up */}
                      <Check
                        className={cn(
                          'h-4 w-4 shrink-0 text-teal-600',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />

                      {/* Company details — no building icon, matches screenshot */}
                      <div className="flex flex-col min-w-0 flex-1">
                        <span
                          className={cn(
                            'text-sm truncate leading-tight',
                            isSelected ? 'font-semibold text-foreground' : 'font-medium text-foreground'
                          )}
                        >
                          {company.name}
                        </span>
                        <span className="text-xs text-teal-600/80 leading-tight truncate">
                          {companySubline(company.code, company.instanceId)}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }

  /* ── Default inline mode (used in page headers) ── */
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          className="h-auto py-1 px-2.5 rounded-md bg-gradient-to-r from-teal-500/15 to-teal-500/10 border-2 border-teal-500/30 shadow-sm hover:shadow-md transition-all duration-200 hover:bg-teal-500/20"
        >
          <div className="flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 text-teal-600" />
            <span className="font-bold text-teal-600 text-xs">{currentCompany.name}</span>
            <ChevronsUpDown className="h-3 w-3 text-teal-600/70" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search companies..." className="h-9" />
          <CommandList>
            <CommandEmpty>No company found.</CommandEmpty>
            <CommandGroup>
              {availableCompanies.map((company) => (
                <CommandItem
                  key={company.id}
                  value={`${company.name} ${company.code}`}
                  onSelect={() => handleSelect(company.id)}
                  className="cursor-pointer"
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      currentCompany.id === company.id ? 'opacity-100 text-teal-600' : 'opacity-0'
                    )}
                  />
                  <div className="flex items-center gap-2 flex-1">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div className="flex flex-col">
                      <span className="font-semibold text-sm">{company.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {companySubline(company.code, company.instanceId)}
                      </span>
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
